import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Plus, Search, Trash2, Download, ImageIcon, ReceiptText, MessageCircle, Pencil, Copy } from "lucide-react";
import { API } from "@/services/api";
import { toast } from "sonner";
import api, { formatApiError } from "@/services/api";
import { useLang } from "@/context/LanguageContext";
import { fmtMoney, fmtDate } from "@/utils/format";
import { buildSenderReceiptMessage, trackingUrl, whatsappSendUrl } from "@/utils/whatsapp";
import { buildReceiptHtml, receiptPngBlob, downloadBlob, RECEIPT_FOOTER, buildReceiptRows } from "@/utils/receipt";
import { useSingleFlightMutation } from "@/hooks/useSingleFlightMutation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import StatusBadge from "@/components/StatusBadge";
import { feePercentFromSettings, feeScheduleRows, splitWithFeeMode } from "@/utils/serviceFee";
import { transferSkipsId } from "@/utils/knownClient";
import { Checkbox } from "@/components/ui/checkbox";

const EMPTY = {
  sender_name: "",
  sender_whatsapp: "",
  client_name: "",
  client_whatsapp: "",
  zone_id: "",
  client_address: "",
  amount: "",
  sender_pays_fee: false,
  known_client: false,
  agent_id: "",
  notes: "",
};

function zoneFullAddress(zone) {
  if (!zone) return "";
  return [zone.name, zone.address, zone.notes].filter(Boolean).join("\n");
}

function resolveZoneIdFromTransfer(tr, zoneList) {
  if (tr?.zone_id) return tr.zone_id;
  const addr = (tr?.client_address || "").trim();
  if (!addr || !zoneList?.length) return "";
  const hit = zoneList.find((z) => {
    const full = zoneFullAddress(z);
    return full === addr || (z.address && addr.includes(z.address));
  });
  return hit?.id || "";
}

function receiptMessageArgs(tr) {
  const total = tr.total_amount != null ? tr.total_amount : (Number(tr.amount || 0) + Number(tr.service_fee || 0));
  return {
    transferId: tr.transfer_id,
    senderName: tr.sender_name,
    senderWhatsapp: tr.sender_whatsapp,
    clientName: tr.client_name,
    clientWhatsapp: tr.client_whatsapp || tr.client_phone,
    clientAddress: tr.client_address,
    netAmount: tr.amount,
    serviceFee: tr.service_fee || 0,
    totalAmount: total,
    trackUrl: trackingUrl(tr.transfer_id),
    knownClient: transferSkipsId(tr),
  };
}

function waReceiptConfirmed(receipt) {
  return !!(receipt?.ok && (receipt.delivery_ack || receipt.server_ack));
}

function WaReceiptStatus({ receipt, defaultPhone, testId, label }) {
  if (receipt?.skipped) return null;
  if (waReceiptConfirmed(receipt)) {
    return (
      <p className="text-emerald-600 text-xs font-medium" data-testid={testId}>
        ✅ {label} voye otomatikman nan{" "}
        <span className="font-mono">{receipt.to || defaultPhone}</span>
        {receipt.mode ? ` (${receipt.mode})` : ""}
        {receipt.delivery_ack
          ? " — livrezon konfime"
          : " — server aksepte (livrezon telefòn poko konfime)"}
      </p>
    );
  }
  if (receipt) {
    return (
      <p className="text-rose-600 text-xs font-medium" data-testid={testId}>
        ❌ {label} pa ale: {receipt.detail || "echwe"}
      </p>
    );
  }
  return null;
}

async function retryWhatsAppReceipt({ transferId, recipient, fallbackPhone }) {
  const r = await api.post("/whatsapp/send-receipt", {
    transfer_id: transferId,
    recipient,
    ...(recipient === "sender" ? { to: fallbackPhone } : {}),
  });
  return {
    confirmed: !!(r.data?.ok && (r.data?.delivery_ack || r.data?.server_ack)),
    meta: {
      ok: !!(r.data?.ok && (r.data?.delivery_ack || r.data?.server_ack)),
      to: r.data?.to || fallbackPhone,
      detail: r.data?.detail || (r.data?.ok ? "ok" : "echwe"),
      mode: r.data?.mode,
      delivery_ack: !!r.data?.delivery_ack,
      server_ack: !!r.data?.server_ack,
      ack_timed_out: !!r.data?.ack_timed_out,
    },
  };
}

const TRANSFER_FORM_DIALOG =
  "bg-card flex h-[100dvh] max-h-[100dvh] w-full max-w-none left-0 top-0 translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-none p-0 sm:left-[50%] sm:top-[50%] sm:h-auto sm:max-h-[92dvh] sm:max-w-lg sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-lg";

function FeeScheduleTable({ rows, senderPays, feePercent, activeAmount, fmtMoney }) {
  const activeRef = React.useRef(null);
  React.useEffect(() => {
    activeRef.current?.scrollIntoView({ block: "nearest" });
  }, [activeAmount, senderPays]);
  return (
    <details className="mt-2 rounded-xl border border-border min-w-0" data-testid="transfer-fee-schedule">
      <summary className="cursor-pointer px-3 py-2 text-xs font-medium">
        Tablo frè ($50 → $100,000)
      </summary>
      <p className="px-3 pb-1 text-[11px] text-muted-foreground">
        Frè a monte sèlman sou $50, $100, $150… $165 rete $15; $200 vin $20.
      </p>
      <div className="max-h-40 sm:max-h-56 overflow-auto">
        <table className="w-full min-w-[16rem] text-[11px] sm:text-xs">
          <thead className="sticky top-0 bg-card text-left">
            <tr>
              <th className="px-2 py-1.5 font-semibold">Montan</th>
              <th className="px-2 py-1.5 font-semibold">Frè</th>
              <th className="px-2 py-1.5 font-semibold">{senderPays ? "Total" : "Resevwa"}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const active = Number(activeAmount) === row.amount;
              return (
                <tr
                  key={row.amount}
                  ref={active ? activeRef : null}
                  className={active ? "bg-emerald-500/15 font-semibold" : "border-t border-border/60"}
                >
                  <td className="px-2 py-1 whitespace-nowrap">{fmtMoney(row.amount)}</td>
                  <td className="px-2 py-1 whitespace-nowrap">{fmtMoney(row.fee)}</td>
                  <td className="px-2 py-1 whitespace-nowrap">{fmtMoney(senderPays ? row.totalPaid : row.receive)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </details>
  );
}

export default function TransfersPage() {
  const { t } = useLang();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [listTab, setListTab] = useState("pending"); // pending | completed
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editTransfer, setEditTransfer] = useState(null); // transfer being corrected
  const [editForm, setEditForm] = useState(EMPTY);
  const [reusePick, setReusePick] = useState("");
  const [page, setPage] = useState(1);
  const [photoModal, setPhotoModal] = useState(null); // { transferId, url } | null
  const [receiptModal, setReceiptModal] = useState(null); // transfer object | null
  const [createdTransfer, setCreatedTransfer] = useState(null); // after create — WhatsApp CTA
  const [cancelDialog, setCancelDialog] = useState(null); // { tid, reason, resolution }
  const [sendingWa, setSendingWa] = useState(false);
  const PAGE = 15;

  const openPhoto = async (transfer) => {
    if (!transfer.client_id_photo) return;
    try {
      const token = localStorage.getItem("tgs-token");
      const res = await fetch(`${API}/uploads/${transfer.client_id_photo}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Foto pa jwenn");
      const buf = await res.arrayBuffer();
      let ct = (res.headers.get("content-type") || "image/jpeg").split(";")[0].trim().toLowerCase();
      if (ct === "image/jpg" || ct === "image/pjpeg") ct = "image/jpeg";
      if (!ct.startsWith("image/")) ct = "image/jpeg";
      const blob = new Blob([buf], { type: ct });
      const url = URL.createObjectURL(blob);
      setPhotoModal({ transferId: transfer.transfer_id, url });
    } catch (e) {
      toast.error("Erè chaje foto");
    }
  };

  React.useEffect(() => {
    return () => { if (photoModal?.url) URL.revokeObjectURL(photoModal.url); };
  }, [photoModal]);

  const { data: transfers = [], isLoading } = useQuery({
    queryKey: ["transfers", search],
    queryFn: async () => {
      const params = {};
      if (search) params.search = search;
      return (await api.get("/transfers", { params })).data;
    },
  });

  const { data: agents = [] } = useQuery({
    queryKey: ["agents"],
    queryFn: async () => (await api.get("/agents")).data,
  });

  const { data: settings = {} } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await api.get("/settings")).data,
  });

  const { data: zones = [] } = useQuery({
    queryKey: ["zones"],
    queryFn: async () => (await api.get("/zones")).data,
  });
  const activeZones = useMemo(
    () => (zones || []).filter((z) => z.active !== false),
    [zones],
  );

  const { data: reuseOptions } = useQuery({
    queryKey: ["transfer-reuse-options"],
    queryFn: async () => (await api.get("/transfers/reuse-options")).data,
    enabled: modalOpen,
  });

  const partyFromTransfer = (tr) => ({
    sender_name: tr.sender_name || "",
    sender_whatsapp: tr.sender_whatsapp || "",
    client_name: tr.client_name || "",
    client_whatsapp: tr.client_whatsapp || tr.client_phone || "",
    zone_id: resolveZoneIdFromTransfer(tr, activeZones),
    client_address: tr.client_address || "",
    amount: "",
    sender_pays_fee: false,
    known_client: transferSkipsId(tr),
    agent_id: tr.agent_id || "",
    notes: "",
  });

  const openCreate = () => {
    setForm(EMPTY);
    setReusePick("");
    setModalOpen(true);
  };

  const openReuse = (tr) => {
    // Copy party fields only — never the old transfer_id. POST /transfers always mints a new No#.
    setForm(partyFromTransfer(tr));
    setReusePick(tr.transfer_id || "");
    setModalOpen(true);
    toast.message(
      `Enfòmasyon kopi soti nan ${tr.transfer_id}. Sa a se yon NOUVO transfè — yon nouvo No# ap kreye.`,
    );
  };

  const openEdit = (tr) => {
    const senderPays = !!tr.sender_pays_fee;
    setEditTransfer(tr);
    setEditForm({
      sender_name: tr.sender_name || "",
      sender_whatsapp: tr.sender_whatsapp || "",
      client_name: tr.client_name || "",
      client_whatsapp: tr.client_whatsapp || tr.client_phone || "",
      zone_id: resolveZoneIdFromTransfer(tr, activeZones),
      client_address: tr.client_address || "",
      amount: senderPays
        ? String(tr.amount ?? "")
        : String(tr.total_amount != null ? tr.total_amount : Number(tr.amount || 0) + Number(tr.service_fee || 0)),
      sender_pays_fee: senderPays,
      agent_id: tr.agent_id || "",
      notes: tr.notes || "",
    });
  };

  const applyReuseSelection = (tid) => {
    setReusePick(tid);
    if (!tid) return;
    const recent = reuseOptions?.recent || [];
    const hit = recent.find((r) => r.transfer_id === tid)
      || transfers.find((r) => r.transfer_id === tid);
    if (!hit) return;
    setForm((prev) => ({
      ...prev,
      ...partyFromTransfer(hit),
      amount: prev.amount,
      notes: prev.notes,
    }));
  };

  const feePercent = feePercentFromSettings(settings);
  const feeRows = useMemo(() => feeScheduleRows(feePercent), [feePercent]);

  const toggleSenderPaysFee = (prevForm, checked) => {
    const amt = parseFloat(prevForm.amount) || 0;
    if (amt <= 0) return { ...prevForm, sender_pays_fee: checked };
    const split = splitWithFeeMode(amt, feePercent, prevForm.sender_pays_fee);
    const nextAmount = checked ? split.netAmount : split.total;
    return {
      ...prevForm,
      sender_pays_fee: checked,
      amount: String(nextAmount),
    };
  };

  const formAmount = parseFloat(form.amount) || 0;
  const { total: formTotal, serviceFee: formFee, netAmount: formNet } = splitWithFeeMode(
    formAmount, feePercent, form.sender_pays_fee,
  );
  const editAmount = parseFloat(editForm.amount) || 0;
  const {
    total: editTotal,
    serviceFee: editFee,
    netAmount: editNet,
  } = splitWithFeeMode(editAmount, feePercent, editForm.sender_pays_fee);

  const pendingList = useMemo(
    () => transfers.filter((x) => x.status === "pending" || x.status === "processing"),
    [transfers]
  );
  const completedList = useMemo(
    () => transfers.filter((x) => x.status === "delivered" || x.status === "canceled"),
    [transfers]
  );
  const activeList = listTab === "pending" ? pendingList : completedList;

  const openWhatsAppReceipt = async (tr) => {
    // WhatsApp kliyan ki voye → kliyan k ap resevwa (open chat with receiver)
    const phone = tr?.client_whatsapp || tr?.client_phone;
    if (!phone) {
      toast.error("Pa gen nimewo WhatsApp benefisyè");
      return;
    }
    setSendingWa(true);
    try {
      const blob = await receiptPngBlob(tr, { fmtMoney, fmtDate });
      if (blob) {
        const file = new File([blob], `TGS-Resi-${tr.transfer_id}.png`, { type: "image/png" });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          const text = buildSenderReceiptMessage(receiptMessageArgs(tr));
          await navigator.share({ files: [file], title: `Resi ${tr.transfer_id}`, text });
          toast.success("Resi pataje");
          return;
        }
        downloadBlob(blob, `TGS-Resi-${tr.transfer_id}.png`);
      }
      const text = buildSenderReceiptMessage(receiptMessageArgs(tr));
      const url = whatsappSendUrl(phone, text);
      if (!url) {
        toast.error("Nimewo WhatsApp envalid");
        return;
      }
      window.open(url, "_blank", "noopener,noreferrer");
      toast.success("Resi telechaje — louvri WhatsApp benefisyè epi tache l");
    } catch (e) {
      toast.error("Erè prepare resi a");
    } finally {
      setSendingWa(false);
    }
  };

  // Full-page printable receipt
  const printReceipt = (tr) => {
    const html = buildReceiptHtml(tr, {
      fmtMoney,
      fmtDate,
      origin: window.location.origin,
      autoPrint: true,
    });
    const w = window.open("", "_blank");
    if (!w) { toast.error("Popup bloke — otorize popup yo"); return; }
    w.document.write(html);
    w.document.close();
  };

  const paged = useMemo(() => activeList.slice((page - 1) * PAGE, page * PAGE), [activeList, page]);
  const totalPages = Math.max(1, Math.ceil(activeList.length / PAGE));

  const createMut = useSingleFlightMutation({
    mutationFn: async () => (await api.post("/transfers", {
      // Never send transfer_id — backend always generates a new No# Transfè.
      sender_name: form.sender_name.trim(),
      sender_whatsapp: form.sender_whatsapp.trim(),
      client_name: form.client_name.trim(),
      client_whatsapp: form.client_whatsapp.trim(),
      client_phone: form.client_whatsapp.trim(),
      zone_id: form.zone_id,
      amount: parseFloat(form.amount) || 0,
      sender_pays_fee: !!form.sender_pays_fee,
      known_client: !!form.known_client,
      agent_id: form.agent_id,
      notes: reusePick
        ? [form.notes || "", `(Enfòmasyon ki soti nan ${reusePick})`].filter(Boolean).join(" ").trim()
        : (form.notes || ""),
    })).data,
    onSuccess: (data) => {
      const wa = data?.whatsapp_receipt;
      const waBen = data?.whatsapp_receipt_beneficiary;
      const waConfirmed = waReceiptConfirmed(wa);
      const waBenConfirmed = waReceiptConfirmed(waBen);
      if (waConfirmed && waBenConfirmed) {
        toast.success(`Transfè ${data.transfer_id} — resi ale nan voye ak benefisyè`);
      } else if (waConfirmed) {
        toast.success(
          `Transfè ${data.transfer_id} — resi WhatsApp ale${
            wa.delivery_ack ? " (livrezon konfime)" : ""
          }`,
        );
        if (waBen && !waBen.skipped && !waBenConfirmed) {
          toast.error(`Resi benefisyè pa ale: ${waBen.detail || "echwe"}`);
        }
      } else if (waBenConfirmed) {
        toast.success(`Transfè ${data.transfer_id} — resi benefisyè ale`);
        if (wa) toast.error(`Resi voye pa ale: ${wa.detail || "echwe"}`);
      } else if (wa || (waBen && !waBen.skipped)) {
        toast.success(`Transfè kreye: ${data.transfer_id}`);
        if (wa) toast.error(`Resi WhatsApp pa ale: ${wa.detail || "echwe"}`);
        if (waBen && !waBen.skipped && !waBenConfirmed) {
          toast.error(`Resi benefisyè pa ale: ${waBen.detail || "echwe"}`);
        }
      } else {
        toast.success(`Transfè kreye: ${data.transfer_id}`);
      }
      qc.invalidateQueries({ queryKey: ["transfers"] });
      qc.invalidateQueries({ queryKey: ["transfer-reuse-options"] });
      setModalOpen(false);
      setForm(EMPTY);
      setReusePick("");
      setCreatedTransfer(data);
    },
    onError: (err) => toast.error(formatApiError(err.response?.data?.detail)),
  });

  const statusMut = useMutation({
    mutationFn: async ({ tid, s, cancel_reason, cancel_resolution }) =>
      (await api.put(`/transfers/${tid}`, {
        status: s,
        ...(s === "canceled" ? { cancel_reason, cancel_resolution } : {}),
      })).data,
    onSuccess: () => {
      toast.success("Estati modifye");
      qc.invalidateQueries({ queryKey: ["transfers"] });
      qc.invalidateQueries({ queryKey: ["agents"] });
    },
    onError: (err) => toast.error(formatApiError(err.response?.data?.detail)),
  });

  const onStatusChange = (t2, newStatus) => {
    if (newStatus === "canceled" && t2.status !== "canceled") {
      setCancelDialog({ tid: t2.transfer_id, reason: "", resolution: "refund" });
      return;
    }
    if (t2.status === "delivered" && newStatus !== "delivered") {
      toast.error("Transfè deja livre — li dwe rete livre. Komisyon an konte sèlman lè li livre.");
      return;
    }
    statusMut.mutate({ tid: t2.transfer_id, s: newStatus });
  };

  const submitCancel = () => {
    const reason = (cancelDialog?.reason || "").trim();
    if (!reason) {
      toast.error("Ekri rezon anilasyon an");
      return;
    }
    statusMut.mutate({
      tid: cancelDialog.tid,
      s: "canceled",
      cancel_reason: reason,
      cancel_resolution: cancelDialog.resolution || "refund",
    }, {
      onSuccess: () => setCancelDialog(null),
    });
  };

  const editMut = useMutation({
    mutationFn: async () => {
      const payload = {
        sender_name: editForm.sender_name.trim(),
        sender_whatsapp: editForm.sender_whatsapp.trim(),
        client_name: editForm.client_name.trim(),
        client_whatsapp: editForm.client_whatsapp.trim(),
        client_phone: editForm.client_whatsapp.trim(),
        zone_id: editForm.zone_id,
        notes: editForm.notes || "",
      };
      if (editTransfer?.status !== "delivered" && editTransfer?.status !== "canceled") {
        payload.amount = parseFloat(editForm.amount) || 0;
        payload.sender_pays_fee = !!editForm.sender_pays_fee;
        if (editForm.agent_id) payload.agent_id = editForm.agent_id;
      }
      return (await api.put(`/transfers/${editTransfer.transfer_id}/details`, payload)).data;
    },
    onSuccess: () => {
      toast.success("Enfòmasyon korije");
      qc.invalidateQueries({ queryKey: ["transfers"] });
      qc.invalidateQueries({ queryKey: ["transfer-reuse-options"] });
      setEditTransfer(null);
    },
    onError: (err) => toast.error(formatApiError(err.response?.data?.detail)),
  });

  const deleteMut = useMutation({
    mutationFn: async (tid) => (await api.delete(`/transfers/${tid}`)).data,
    onSuccess: () => { toast.success("Transfè efase"); qc.invalidateQueries({ queryKey: ["transfers"] }); },
  });

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex items-start justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-heading font-bold">{t("transfers")}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Jere transfè yo — korije non si gen erè, oswa reutilize menm enfòmasyon pou yon nouvo transfè.
          </p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Button
            variant="outline"
            data-testid="export-transfers-btn"
            className="gap-2 flex-1 sm:flex-none"
            onClick={async () => {
              const token = localStorage.getItem("tgs-token");
              const res = await fetch(`${API}/export/transfers.csv`, { headers: { Authorization: `Bearer ${token}` } });
              const blob = await res.blob();
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a"); a.href = url; a.download = "tgs_transfers.csv"; a.click(); URL.revokeObjectURL(url);
              toast.success("CSV telechaje");
            }}
          >
            <Download className="h-4 w-4" /> CSV
          </Button>
          <Button onClick={openCreate} data-testid="new-transfer-btn" className="bg-brand-blue hover:bg-brand-blueHover text-white gap-2 flex-1 sm:flex-none">
            <Plus className="h-4 w-4" /> {t("new_transfer")}
          </Button>
        </div>
      </motion.div>

      <div className="rounded-2xl border border-border bg-card">
        <div className="p-4 border-b border-border flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[160px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="TG, non, telefòn, CIN..." className="pl-9 h-9" data-testid="transfer-search-input" />
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant={listTab === "pending" ? "default" : "outline"}
              className={listTab === "pending" ? "bg-brand-blue text-white" : ""}
              onClick={() => { setListTab("pending"); setPage(1); }}
              data-testid="transfers-tab-pending"
            >
              An atant ({pendingList.length})
            </Button>
            <Button
              size="sm"
              variant={listTab === "completed" ? "default" : "outline"}
              className={listTab === "completed" ? "bg-brand-blue text-white" : ""}
              onClick={() => { setListTab("completed"); setPage(1); }}
              data-testid="transfers-tab-completed"
            >
              Konplete ({completedList.length})
            </Button>
          </div>
          <div className="text-xs text-muted-foreground ml-auto">{activeList.length} nan lis sa a</div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>No# Transfè</TableHead>
                <TableHead>{t("client_name")}</TableHead>
                <TableHead>{t("phone")}</TableHead>
                <TableHead>{t("amount")}</TableHead>
                <TableHead>Ajan</TableHead>
                <TableHead>Komisyon</TableHead>
                <TableHead>Foto ID</TableHead>
                <TableHead>Dat</TableHead>
                <TableHead>Estati</TableHead>
                <TableHead className="text-right">Aksyon</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && [...Array(4)].map((_, i) => (
                <TableRow key={i}><TableCell colSpan={10}><Skeleton className="h-8" /></TableCell></TableRow>
              ))}
              {!isLoading && paged.length === 0 && (
                <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground py-10">Pa gen transfè</TableCell></TableRow>
              )}
              {paged.map((t2) => (
                <TableRow key={t2.transfer_id} data-testid={`transfer-row-${t2.transfer_id}`}>
                  <TableCell className="font-mono text-xs">
                    {t2.transfer_id}
                    {transferSkipsId(t2) && (
                      <div className="font-sans text-[10px] text-emerald-700">San pyès</div>
                    )}
                  </TableCell>
                  <TableCell className="font-medium">{t2.client_name}</TableCell>
                  <TableCell className="text-sm">{t2.client_phone}</TableCell>
                  <TableCell className="font-semibold">{fmtMoney(t2.amount)}</TableCell>
                  <TableCell className="text-xs">{t2.agent_name}</TableCell>
                  <TableCell className="tabular-nums text-sm font-semibold text-brand-gold" data-testid={`transfer-comm-${t2.transfer_id}`}>
                    {t2.status === "delivered" ? fmtMoney(t2.agent_profit ?? 0) : "—"}
                  </TableCell>
                  <TableCell>
                    {t2.client_id_photo ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 gap-1.5 border-brand-blue/30 text-brand-blue hover:bg-brand-blue/10"
                        onClick={() => openPhoto(t2)}
                        data-testid={`view-photo-${t2.transfer_id}`}
                      >
                        <ImageIcon className="h-3.5 w-3.5" /> Wè
                      </Button>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{fmtDate(t2.created_at)}</TableCell>
                  <TableCell>
                    <Select value={t2.status} onValueChange={(v) => onStatusChange(t2, v)}>
                      <SelectTrigger className="h-8 w-[130px] bg-background" data-testid={`transfer-status-${t2.transfer_id}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-popover">
                        <SelectItem value="pending"><StatusBadge status="pending" /></SelectItem>
                        <SelectItem value="processing"><StatusBadge status="processing" /></SelectItem>
                        <SelectItem value="delivered"><StatusBadge status="delivered" /></SelectItem>
                        <SelectItem value="canceled"><StatusBadge status="canceled" /></SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEdit(t2)}
                      data-testid={`edit-transfer-${t2.transfer_id}`}
                      title="Korije non / enfòmasyon"
                    >
                      <Pencil className="h-4 w-4 text-brand-gold" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openReuse(t2)}
                      data-testid={`reuse-transfer-${t2.transfer_id}`}
                      title="Nouvo transfè ak menm enfòmasyon (nouvo No#)"
                    >
                      <Copy className="h-4 w-4 text-brand-blue" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setReceiptModal(t2)}
                      data-testid={`receipt-btn-${t2.transfer_id}`}
                      title="Resi"
                    >
                      <ReceiptText className="h-4 w-4 text-brand-blue" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => window.confirm("Konfime efasman?") && deleteMut.mutate(t2.transfer_id)}>
                      <Trash2 className="h-4 w-4 text-rose-500" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {totalPages > 1 && (
          <div className="p-3 border-t border-border flex items-center justify-between text-xs">
            <div>Paj {page} / {totalPages}</div>
            <div className="space-x-2">
              <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage(page - 1)}>Anvan</Button>
              <Button size="sm" variant="outline" disabled={page === totalPages} onClick={() => setPage(page + 1)}>Apre</Button>
            </div>
          </div>
        )}
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className={TRANSFER_FORM_DIALOG}>
          <DialogHeader className="shrink-0 px-4 pt-4 pb-2 pr-12 text-left">
            <DialogTitle className="text-left text-base sm:text-lg leading-snug">
              {reusePick ? "Nouvo transfè (menm enfòmasyon)" : t("new_transfer")}
            </DialogTitle>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-2">
          <div className="grid grid-cols-1 gap-3 min-w-0">
            {reusePick && (
              <div className="sm:col-span-2 rounded-xl border border-brand-gold/40 bg-brand-gold/10 p-3 text-sm" data-testid="reuse-new-tid-banner">
                Ou ap kreye yon <strong>nouvo</strong> transfè ak menm moun yo.
                Sistèm nan ap bay yon <strong>nouvo No# Transfè</strong> —
                pa menm ak <span className="font-mono">{reusePick}</span>.
              </div>
            )}

            <div className="sm:col-span-2 rounded-xl border border-border bg-secondary/30 p-3 space-y-2">
              <Label>Itilize menm enfòmasyon (moun ki deja nan sistèm nan)</Label>
              <Select value={reusePick || "__none"} onValueChange={(v) => applyReuseSelection(v === "__none" ? "" : v)}>
                <SelectTrigger className="bg-background" data-testid="transfer-reuse-select">
                  <SelectValue placeholder="Chwazi yon transfè anvan…" />
                </SelectTrigger>
                <SelectContent className="bg-popover max-h-64">
                  <SelectItem value="__none">— Ranpli manyèlman —</SelectItem>
                  {(reuseOptions?.recent || []).map((r) => (
                    <SelectItem key={r.transfer_id} value={r.transfer_id}>
                      {r.sender_name || "—"} → {r.client_name || "—"} ({r.transfer_id})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">
                Kopi non / WhatsApp / adrès sèlman. Lè w sove, yon <strong>nouvo No#</strong> ap kreye (pa kopi nimewo a).
              </p>
            </div>

            <div className="sm:col-span-2 text-xs font-semibold uppercase tracking-wider text-brand-blue">Moun ki voye Zelle la</div>
            <div className="sm:col-span-2">
              <Label>Non konplè</Label>
              <Input value={form.sender_name} onChange={(e) => setForm({ ...form, sender_name: e.target.value })} data-testid="transfer-form-sender-name" placeholder="Non konplè sender" />
            </div>
            <div className="sm:col-span-2">
              <Label>WhatsApp kliyan ki voye (pou resevwa resi) *</Label>
              <Input value={form.sender_whatsapp} onChange={(e) => setForm({ ...form, sender_whatsapp: e.target.value })} placeholder="+509 + 8 chif (egz. +50937123456)" data-testid="transfer-form-sender-whatsapp" />
              <p className="text-[11px] text-muted-foreground mt-1">Resi a ale otomatikman sou nimewo sa a.</p>
            </div>

            <div className="sm:col-span-2 text-xs font-semibold uppercase tracking-wider text-brand-gold pt-1">Moun k ap resevwa a</div>
            <div className="sm:col-span-2">
              <Label>Non konplè</Label>
              <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="transfer-form-client-name" />
              <div className="mt-2 flex items-start gap-2">
                <Checkbox
                  id="known-client"
                  checked={!!form.known_client}
                  onCheckedChange={(v) => setForm((prev) => ({ ...prev, known_client: !!v }))}
                  data-testid="transfer-form-known-client"
                />
                <Label htmlFor="known-client" className="text-sm font-normal leading-snug cursor-pointer">
                  Koche si moun sa a pa bezwen bay pyès
                </Label>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">
                Se ou menm ki koche sa a. Lè l koche, No# la ap TG + 4 chif epi ajan an apwouve san foto.
                Si w pa koche l, ajan an voye foto pyès la.
              </p>
            </div>
            <div className="sm:col-span-2">
              <Label>WhatsApp benefisyè <span className="text-muted-foreground font-normal">(opsyonèl — ka vid)</span></Label>
              <Input value={form.client_whatsapp} onChange={(e) => setForm({ ...form, client_whatsapp: e.target.value })} placeholder="Ka rete vid" data-testid="transfer-form-client-whatsapp" />
              <p className="text-[11px] text-muted-foreground mt-1">Pa obligatwa. Resi a ale sou WhatsApp moun ki voye a.</p>
            </div>
            <div className="sm:col-span-2">
              <Label>Zon / adrès pou pase pran *</Label>
              <Select value={form.zone_id} onValueChange={(v) => setForm({ ...form, zone_id: v })}>
                <SelectTrigger className="bg-background w-full min-w-0 [&>span]:line-clamp-1" data-testid="transfer-form-zone">
                  <SelectValue placeholder="Chwazi kote kliyan an ap pase pran lajan an" />
                </SelectTrigger>
                <SelectContent className="bg-popover max-h-72">
                  {activeZones.map((z) => (
                    <SelectItem key={z.id} value={z.id}>
                      {z.name}{z.address ? ` — ${z.address}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.zone_id && (
                <div className="mt-2 rounded-lg border border-border bg-secondary/30 p-3 text-xs whitespace-pre-line" data-testid="transfer-form-zone-preview">
                  {zoneFullAddress(activeZones.find((z) => z.id === form.zone_id))}
                </div>
              )}
              {!activeZones.length && (
                <p className="text-[11px] text-amber-600 mt-1">Pa gen zon aktif — ajoute yo nan Admin → Zon.</p>
              )}
            </div>

            <div className="sm:col-span-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground pt-1">Montan & ajan</div>
            <div className="sm:col-span-2">
              <Label>{form.sender_pays_fee ? "Montan benefisyè ap jwenn (USD)" : "Montan total (USD)"}</Label>
              <Input type="number" inputMode="decimal" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="transfer-form-amount" />
              <div className="mt-2 flex items-start gap-2">
                <Checkbox
                  id="sender-pays-fee"
                  checked={!!form.sender_pays_fee}
                  onCheckedChange={(v) => setForm((prev) => toggleSenderPaysFee(prev, !!v))}
                  data-testid="transfer-form-sender-pays-fee"
                />
                <Label htmlFor="sender-pays-fee" className="text-sm font-normal leading-snug cursor-pointer">
                  Sender peye frè a (frè a ap ajoute sou montan benefisyè a dwe jwenn)
                </Label>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {form.sender_pays_fee
                  ? "Antre montan benefisyè a dwe jwenn. Frè a monte sèlman chak $50 fini ($165 → $15, $200 → $20) epi li ajoute sou total. Egz. $800 → frè $80 → total $880."
                  : "Antre total Zelle. Frè a monte sèlman chak $50 fini ($150 → $15, $165 → $15, $200 → $20) epi li soti nan total la. Egz. $800 → frè $80 → benefisyè jwenn $720."}
              </p>
              {formAmount > 0 && (
                <div className="mt-2 rounded-xl border border-border bg-secondary/40 p-3 text-xs space-y-1" data-testid="transfer-fee-preview">
                  {form.sender_pays_fee ? (
                    <>
                      <div className="flex justify-between"><span>Montan benefisyè</span><span className="font-semibold">{fmtMoney(formAmount)}</span></div>
                      <div className="flex justify-between"><span>Frè ({feePercent}%)</span><span className="font-semibold text-amber-600">+ {fmtMoney(formFee)}</span></div>
                      <div className="flex justify-between border-t border-border pt-1"><span>Total Zelle</span><span className="font-bold text-emerald-600">{fmtMoney(formTotal)}</span></div>
                    </>
                  ) : (
                    <>
                      <div className="flex justify-between"><span>Montan total</span><span className="font-semibold">{fmtMoney(formTotal)}</span></div>
                      <div className="flex justify-between"><span>Frè ({feePercent}%)</span><span className="font-semibold text-amber-600">− {fmtMoney(formFee)}</span></div>
                      <div className="flex justify-between border-t border-border pt-1"><span>Montan li ap jwenn</span><span className="font-bold text-emerald-600">{fmtMoney(formNet)}</span></div>
                    </>
                  )}
                </div>
              )}
              <FeeScheduleTable
                rows={feeRows}
                senderPays={!!form.sender_pays_fee}
                feePercent={feePercent}
                activeAmount={formAmount}
                fmtMoney={fmtMoney}
              />
            </div>
            <div className="sm:col-span-2">
              <Label>{t("assign_to_agent")}</Label>
              <Select value={form.agent_id} onValueChange={(v) => setForm({ ...form, agent_id: v })}>
                <SelectTrigger className="bg-background w-full min-w-0 [&>span]:line-clamp-1" data-testid="transfer-form-agent"><SelectValue placeholder="Chwazi yon ajan" /></SelectTrigger>
                <SelectContent className="bg-popover">
                  {agents.filter((a) => a.active).map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name} — {a.phone}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2"><Label>{t("notes")}</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          </div>
          <DialogFooter className="shrink-0 gap-2 border-t border-border bg-card px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:flex-row sm:justify-end">
            <Button variant="outline" className="w-full sm:w-auto h-11" onClick={() => setModalOpen(false)}>{t("cancel")}</Button>
            <Button
              className="bg-brand-blue hover:bg-brand-blueHover text-white w-full sm:w-auto h-11"
              onClick={() => createMut.mutate()}
              disabled={
                createMut.isPending
                || !form.agent_id
                || !form.amount
                || !form.sender_name.trim()
                || !form.sender_whatsapp.trim()
                || !form.client_name.trim()
                || !form.zone_id
              }
              data-testid="transfer-form-save"
            >
              {createMut.isPending ? t("loading") : (reusePick ? "Kreye nouvo transfè" : t("create"))}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editTransfer} onOpenChange={(o) => !o && setEditTransfer(null)}>
        <DialogContent className={TRANSFER_FORM_DIALOG} data-testid="edit-transfer-dialog">
          <DialogHeader className="shrink-0 px-4 pt-4 pb-2 pr-12 text-left">
            <DialogTitle className="text-left text-base sm:text-lg leading-snug break-all">
              Korije enfòmasyon —{" "}
              <span className="font-mono text-brand-gold">{editTransfer?.transfer_id}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-2">
          <p className="text-xs text-muted-foreground mb-3">
            Korije non oswa kontak si gen erè. Apre livrezon, montan/ajan pa chanje — non ak adrès wi.
          </p>
          <div className="grid grid-cols-1 gap-3 min-w-0">
            <div className="sm:col-span-2 text-xs font-semibold uppercase tracking-wider text-brand-blue">Moun ki voye</div>
            <div className="sm:col-span-2">
              <Label>Non konplè</Label>
              <Input
                value={editForm.sender_name}
                onChange={(e) => setEditForm({ ...editForm, sender_name: e.target.value })}
                data-testid="edit-transfer-sender-name"
              />
            </div>
            <div className="sm:col-span-2">
              <Label>WhatsApp sender</Label>
              <Input
                value={editForm.sender_whatsapp}
                onChange={(e) => setEditForm({ ...editForm, sender_whatsapp: e.target.value })}
                data-testid="edit-transfer-sender-whatsapp"
              />
            </div>
            <div className="sm:col-span-2 text-xs font-semibold uppercase tracking-wider text-brand-gold pt-1">Benefisyè</div>
            <div className="sm:col-span-2">
              <Label>Non konplè</Label>
              <Input
                value={editForm.client_name}
                onChange={(e) => setEditForm({ ...editForm, client_name: e.target.value })}
                data-testid="edit-transfer-client-name"
              />
            </div>
            <div className="sm:col-span-2">
              <Label>WhatsApp benefisyè (opsyonèl)</Label>
              <Input
                value={editForm.client_whatsapp}
                onChange={(e) => setEditForm({ ...editForm, client_whatsapp: e.target.value })}
                data-testid="edit-transfer-client-whatsapp"
              />
            </div>
            <div className="sm:col-span-2">
              <Label>Zon / adrès pou pase pran *</Label>
              <Select value={editForm.zone_id} onValueChange={(v) => setEditForm({ ...editForm, zone_id: v })}>
                <SelectTrigger className="bg-background w-full min-w-0 [&>span]:line-clamp-1" data-testid="edit-transfer-zone">
                  <SelectValue placeholder="Chwazi zon" />
                </SelectTrigger>
                <SelectContent className="bg-popover max-h-72">
                  {activeZones.map((z) => (
                    <SelectItem key={z.id} value={z.id}>
                      {z.name}{z.address ? ` — ${z.address}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {editForm.zone_id && (
                <div className="mt-2 rounded-lg border border-border bg-secondary/30 p-3 text-xs whitespace-pre-line">
                  {zoneFullAddress(activeZones.find((z) => z.id === editForm.zone_id))}
                </div>
              )}
            </div>
            {editTransfer?.status !== "delivered" && editTransfer?.status !== "canceled" && (
              <>
                <div className="sm:col-span-2">
                  <Label>{editForm.sender_pays_fee ? "Montan benefisyè ap jwenn (USD)" : "Montan total (USD)"}</Label>
                  <Input
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    value={editForm.amount}
                    onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                    data-testid="edit-transfer-amount"
                  />
                  <div className="mt-2 flex items-start gap-2">
                    <Checkbox
                      id="edit-sender-pays-fee"
                      checked={!!editForm.sender_pays_fee}
                      onCheckedChange={(v) => setEditForm((prev) => toggleSenderPaysFee(prev, !!v))}
                      data-testid="edit-transfer-sender-pays-fee"
                    />
                    <Label htmlFor="edit-sender-pays-fee" className="text-sm font-normal leading-snug cursor-pointer">
                      Sender peye frè a
                    </Label>
                  </div>
                  {editAmount > 0 && (
                    <div className="mt-2 rounded-xl border border-border bg-secondary/40 p-3 text-xs space-y-1">
                      {editForm.sender_pays_fee ? (
                        <>
                          <div className="flex justify-between"><span>Montan benefisyè</span><span>{fmtMoney(editAmount)}</span></div>
                          <div className="flex justify-between"><span>Frè</span><span>+ {fmtMoney(editFee)}</span></div>
                          <div className="flex justify-between border-t border-border pt-1"><span>Total Zelle</span><span className="font-bold">{fmtMoney(editTotal)}</span></div>
                        </>
                      ) : (
                        <>
                          <div className="flex justify-between"><span>Montan total</span><span>{fmtMoney(editTotal)}</span></div>
                          <div className="flex justify-between"><span>Frè</span><span>− {fmtMoney(editFee)}</span></div>
                          <div className="flex justify-between border-t border-border pt-1"><span>Montan li ap jwenn</span><span className="font-bold">{fmtMoney(editNet)}</span></div>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <div className="sm:col-span-2">
                  <Label>{t("assign_to_agent")}</Label>
                  <Select value={editForm.agent_id} onValueChange={(v) => setEditForm({ ...editForm, agent_id: v })}>
                    <SelectTrigger className="bg-background w-full min-w-0 [&>span]:line-clamp-1" data-testid="edit-transfer-agent"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-popover">
                      {agents.filter((a) => a.active).map((a) => (
                        <SelectItem key={a.id} value={a.id}>{a.name} — {a.phone}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            <div className="sm:col-span-2">
              <Label>{t("notes")}</Label>
              <Input value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} />
            </div>
          </div>
          </div>
          <DialogFooter className="shrink-0 gap-2 border-t border-border bg-card px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:flex-row sm:justify-end">
            <Button variant="outline" className="w-full sm:w-auto h-11" onClick={() => setEditTransfer(null)}>{t("cancel")}</Button>
            <Button
              className="bg-brand-blue hover:bg-brand-blueHover text-white w-full sm:w-auto h-11"
              onClick={() => editMut.mutate()}
              disabled={
                editMut.isPending
                || !editForm.sender_name.trim()
                || !editForm.sender_whatsapp.trim()
                || !editForm.client_name.trim()
                || !editForm.zone_id
              }
              data-testid="edit-transfer-save"
            >
              {editMut.isPending ? t("loading") : "Sove koreksyon"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!photoModal} onOpenChange={(o) => !o && setPhotoModal(null)}>
        <DialogContent className="bg-card max-w-2xl">
          <DialogHeader>
            <DialogTitle>Foto Pyès Idantite — <span className="font-mono text-brand-gold">{photoModal?.transferId}</span></DialogTitle>
          </DialogHeader>
          {photoModal?.url && (
            <div className="rounded-xl overflow-hidden border border-border bg-slate-950 flex items-center justify-center">
              <img
                src={photoModal.url}
                alt="Pyès idantite kliyan"
                className="max-h-[70vh] w-auto object-contain"
                data-testid="id-photo-image"
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPhotoModal(null)}>{t("cancel")}</Button>
            {photoModal?.url && (
              <a href={photoModal.url} download={`${photoModal.transferId}_id.jpg`}>
                <Button className="bg-brand-blue hover:bg-brand-blueHover text-white">Telechaje</Button>
              </a>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!cancelDialog} onOpenChange={(o) => !o && setCancelDialog(null)}>
        <DialogContent className="bg-card max-w-md">
          <DialogHeader>
            <DialogTitle>Anile transfè — {cancelDialog?.tid}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Moun ki voye lajan an ap resevwa rezon an ak solisyon sou WhatsApp (si gen nimewo).
          </p>
          <div className="space-y-2">
            <Label>Rezon anilasyon *</Label>
            <Textarea
              value={cancelDialog?.reason || ""}
              onChange={(e) => setCancelDialog((d) => ({ ...d, reason: e.target.value }))}
              placeholder="Egz. benefisyè pa gen pyès idantite valab…"
              rows={3}
              data-testid="cancel-reason-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Solisyon pou moun ki voye a</Label>
            <Select
              value={cancelDialog?.resolution || "refund"}
              onValueChange={(v) => setCancelDialog((d) => ({ ...d, resolution: v }))}
            >
              <SelectTrigger className="bg-background" data-testid="cancel-resolution-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-popover">
                <SelectItem value="refund">Ranbousman</SelectItem>
                <SelectItem value="reactivate">Re-aktive transfè a</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialog(null)}>{t("cancel")}</Button>
            <Button
              className="bg-rose-600 hover:bg-rose-700 text-white"
              onClick={submitCancel}
              disabled={statusMut.isPending}
              data-testid="confirm-cancel-transfer"
            >
              Konfime anilasyon
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!receiptModal} onOpenChange={(o) => !o && setReceiptModal(null)}>
        <DialogContent className="bg-card max-w-md">
          <DialogHeader>
            <DialogTitle>Resi Transfè Zelle</DialogTitle>
          </DialogHeader>
          {receiptModal && (
            <div className="relative overflow-hidden rounded-2xl border border-border bg-background p-5" data-testid="receipt-card">
              <div className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center" aria-hidden="true">
                <span
                  className="rotate-[-32deg] whitespace-nowrap font-heading text-5xl font-extrabold uppercase tracking-[0.2em] text-brand-gold/20"
                  data-testid="receipt-watermark"
                >
                  PAID
                </span>
              </div>
              <div className="relative z-10 text-center border-b-2 border-brand-blue/40 pb-3 mb-4">
                <img src="/tgs-logo.png" alt="TGS" className="mx-auto mb-1.5 h-16 w-16 object-contain" />
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-muted-foreground">Takara Global Services</div>
                <div className="font-heading font-extrabold text-lg mt-0.5">Resi Transfè Zelle</div>
              </div>
              <dl className="relative z-10 space-y-2.5 text-sm">
                {buildReceiptRows(receiptModal, fmtMoney, fmtDate).map(([k, v], i) => (
                  <div key={i} className="flex items-start justify-between gap-4 border-b border-border/60 pb-2">
                    <dt className="text-muted-foreground font-medium">{k}</dt>
                    <dd className="font-semibold text-right" data-testid={k === "No# Transfè" ? "receipt-transfer-id" : undefined}>{v}</dd>
                  </div>
                ))}
              </dl>
              <p className="relative z-10 mt-4 text-xs font-medium leading-relaxed rounded-xl border border-brand-blue/30 bg-brand-blue/5 p-3" data-testid="receipt-footer-note">
                {RECEIPT_FOOTER}
              </p>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2 sm:justify-end">
            <Button variant="outline" onClick={() => setReceiptModal(null)}>{t("cancel")}</Button>
            <Button
              variant="outline"
              className="gap-2"
              onClick={async () => {
                try {
                  const blob = await receiptPngBlob(receiptModal, { fmtMoney, fmtDate });
                  if (blob) downloadBlob(blob, `TGS-Resi-${receiptModal.transfer_id}.png`);
                  else toast.error("Pa ka jenere imaj");
                } catch {
                  toast.error("Erè telechaje resi");
                }
              }}
              data-testid="receipt-download-png-btn"
            >
              <Download className="h-4 w-4" /> Telechaje PNG
            </Button>
            {receiptModal?.client_whatsapp || receiptModal?.client_phone ? (
              <Button
                variant="outline"
                className="gap-2 border-emerald-500/40 text-emerald-600 hover:bg-emerald-500/10"
                onClick={() => openWhatsAppReceipt(receiptModal)}
                disabled={sendingWa}
                data-testid="receipt-whatsapp-btn"
              >
                <MessageCircle className="h-4 w-4" /> {sendingWa ? "Ap prepare..." : "Voye → WhatsApp benefisyè"}
              </Button>
            ) : null}
            <Button
              className="bg-brand-blue hover:bg-brand-blueHover text-white gap-2"
              onClick={() => printReceipt(receiptModal)}
              data-testid="receipt-print-btn"
            >
              <ReceiptText className="h-4 w-4" /> Enprime (plen fèy)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!createdTransfer} onOpenChange={(o) => !o && setCreatedTransfer(null)}>
        <DialogContent className="bg-card max-w-md">
          <DialogHeader>
            <DialogTitle>✅ Transfè kreye</DialogTitle>
          </DialogHeader>
          {createdTransfer && (
            <div className="space-y-2 text-sm" data-testid="created-transfer-summary">
              <p>No# Transfè: <span className="font-mono font-bold text-brand-gold">{createdTransfer.transfer_id}</span></p>
              <p>Ajan: <span className="font-medium">{createdTransfer.agent_name || agents.find((a) => a.id === createdTransfer.agent_id)?.name || "—"}</span></p>
              <p>Moun ki voye: {createdTransfer.sender_name || "—"}</p>
              <p className="text-xs">WhatsApp kliyan (resi): <span className="font-mono">{createdTransfer.sender_whatsapp || "—"}</span></p>
              <p>Moun k ap resevwa: {createdTransfer.client_name || "—"}</p>
              <p className="text-xs">WhatsApp benefisyè: <span className="font-mono">{createdTransfer.client_whatsapp || createdTransfer.client_phone || "—"}</span></p>
              {createdTransfer.client_address && (
                <div className="text-xs whitespace-pre-line rounded-lg border border-border bg-secondary/30 p-2">
                  <span className="font-medium">Kote pou pase pran lajan an:</span>
                  {"\n"}
                  {createdTransfer.client_address}
                </div>
              )}
              <div className="rounded-xl border border-border bg-secondary/30 p-3 space-y-1 text-xs">
                <div className="flex justify-between"><span>Montan pou bay</span><span className="font-semibold">{fmtMoney(createdTransfer.amount)}</span></div>
                <div className="flex justify-between"><span>Frè sèvis ({createdTransfer.fee_percent ?? feePercent}%)</span><span className="font-semibold">{fmtMoney(createdTransfer.service_fee || 0)}</span></div>
                <div className="flex justify-between border-t border-border pt-1"><span>Montan total</span><span className="font-bold">{fmtMoney(createdTransfer.total_amount != null ? createdTransfer.total_amount : (Number(createdTransfer.amount || 0) + Number(createdTransfer.service_fee || 0)))}</span></div>
              </div>
              <WaReceiptStatus
                receipt={createdTransfer.whatsapp_receipt}
                defaultPhone={createdTransfer.sender_whatsapp}
                testId="created-wa-receipt-ok"
                label="Resi WhatsApp (voye)"
              />
              <WaReceiptStatus
                receipt={createdTransfer.whatsapp_receipt_beneficiary}
                defaultPhone={createdTransfer.client_whatsapp || createdTransfer.client_phone}
                testId="created-wa-receipt-beneficiary-ok"
                label="Resi WhatsApp (benefisyè)"
              />
              {!createdTransfer.whatsapp_receipt && !createdTransfer.sender_whatsapp && (
                <p className="text-muted-foreground text-xs">
                  Ou ka voye resi a sou WhatsApp kliyan ki voye a.
                </p>
              )}
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setCreatedTransfer(null)}>Fèmen</Button>
            {createdTransfer?.sender_whatsapp && (
              <Button
                className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                onClick={async () => {
                  if (!createdTransfer?.transfer_id) return;
                  setSendingWa(true);
                  try {
                    const { confirmed, meta } = await retryWhatsAppReceipt({
                      transferId: createdTransfer.transfer_id,
                      recipient: "sender",
                      fallbackPhone: createdTransfer.sender_whatsapp,
                    });
                    if (confirmed) {
                      toast.success("Resi voye ale sou WhatsApp kliyan");
                    } else {
                      toast.error(meta.detail || "Resi pa ale");
                    }
                    setCreatedTransfer({ ...createdTransfer, whatsapp_receipt: meta });
                  } catch (err) {
                    toast.error(formatApiError(err.response?.data?.detail) || "Resi pa ale");
                  } finally {
                    setSendingWa(false);
                  }
                }}
                disabled={sendingWa}
                data-testid="created-whatsapp-receipt-btn"
              >
                <MessageCircle className="h-4 w-4" />{" "}
                {sendingWa
                  ? "Ap voye..."
                  : createdTransfer?.whatsapp_receipt?.ok
                    ? "Voye resi ankò → voye"
                    : "Eseye resi → WhatsApp voye"}
              </Button>
            )}
            {(createdTransfer?.client_whatsapp || createdTransfer?.client_phone) &&
              (createdTransfer.client_whatsapp || createdTransfer.client_phone) !==
                createdTransfer.sender_whatsapp && (
              <Button
                className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                onClick={async () => {
                  if (!createdTransfer?.transfer_id) return;
                  setSendingWa(true);
                  try {
                    const fallback =
                      createdTransfer.client_whatsapp || createdTransfer.client_phone;
                    const { confirmed, meta } = await retryWhatsAppReceipt({
                      transferId: createdTransfer.transfer_id,
                      recipient: "beneficiary",
                      fallbackPhone: fallback,
                    });
                    if (confirmed) {
                      toast.success("Resi ale sou WhatsApp benefisyè");
                    } else {
                      toast.error(meta.detail || "Resi pa ale");
                    }
                    setCreatedTransfer({
                      ...createdTransfer,
                      whatsapp_receipt_beneficiary: meta,
                    });
                  } catch (err) {
                    toast.error(formatApiError(err.response?.data?.detail) || "Resi pa ale");
                  } finally {
                    setSendingWa(false);
                  }
                }}
                disabled={sendingWa}
                data-testid="created-whatsapp-receipt-beneficiary-btn"
              >
                <MessageCircle className="h-4 w-4" />{" "}
                {sendingWa
                  ? "Ap voye..."
                  : createdTransfer?.whatsapp_receipt_beneficiary?.ok
                    ? "Voye resi ankò → benefisyè"
                    : "Eseye resi → WhatsApp benefisyè"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
